<div>
  <form [formGroup]="storefrontForm" class="space-y-6">
    <!-- ── Name, URL, description ── -->
    <section hlmCard>
      <div hlmCardHeader>
        <h2 hlmCardTitle>Deine Shopseite</h2>
        <p hlmCardDescription>
          Dein Shop ist erreichbar unter
          <a
            [href]="getStorefrontUrl()"
            target="_blank"
            class="break-all underline underline-offset-4">
            {{ getStorefrontDisplayUrl() }}
          </a>
        </p>
      </div>

      <div hlmCardContent class="space-y-4">
        <div hlmField>
          <label hlmFieldLabel for="storefront-suburl">Storefront-URL *</label>
          <hlm-input-group class="w-full">
            <hlm-input-group-addon>
              <span hlmInputGroupText>https://</span>
            </hlm-input-group-addon>
            <input
              hlmInputGroupInput
              id="storefront-suburl"
              type="text"
              formControlName="suburl"
              placeholder="mein-shop"
              [attr.aria-invalid]="isInvalid('suburl') ? 'true' : null" />
            <hlm-input-group-addon align="inline-end">
              <span hlmInputGroupText>.{{ getStorefrontBaseDomain() }}</span>
            </hlm-input-group-addon>
          </hlm-input-group>
          @if (isInvalid('suburl')) {
            <p hlmFieldDescription class="text-destructive">
              @if (storefrontForm.get('suburl')?.errors?.['required']) {
                Storefront-URL ist erforderlich
              } @else {
                Nur Buchstaben, Zahlen und Bindestriche erlaubt
              }
            </p>
          }
        </div>

        <div hlmField id="section-description">
          <label hlmFieldLabel for="storefront-description">Beschreibung *</label>
          <textarea
            hlmTextarea
            id="storefront-description"
            rows="4"
            formControlName="description"
            placeholder="Beschreibe deinen Gutschein-Shop…"
            class="w-full"></textarea>
        </div>
      </div>

      <div hlmCardFooter class="justify-end border-t pt-4">
        <app-save-button
          #basicInfoSaveButton
          size="sm"
          [disabled]="!storefrontForm.valid || !formChanged"
          [loading]="loading"
          (clickEvent)="onSubmitStorefrontDetails()" />
      </div>
    </section>

    <!-- ── Business address ── -->
    <section hlmCard>
      <div hlmCardHeader>
        <h2 hlmCardTitle>Geschäftsadresse</h2>
        <p hlmCardDescription>
          Diese Adresse wird auf Gutscheinen und im Gutschein-Shop angezeigt.
        </p>
      </div>

      <div hlmCardContent class="space-y-4">
        <div hlmItem variant="outline">
          <div hlmItemContent>
            <span hlmItemTitle>Rechnungsadresse verwenden</span>
            <span hlmItemDescription>
              Deaktivieren, wenn dein Geschäft an einer anderen Adresse liegt.
            </span>
          </div>
          <div hlmItemActions>
            <hlm-switch
              aria-label="Rechnungsadresse als Geschäftsadresse verwenden"
              formControlName="use_billing_as_business" />
          </div>
        </div>

        @if (storefrontForm.get('use_billing_as_business')?.value) {
          <!-- Read-only mirror of the billing address -->
          <dl class="grid gap-x-6 gap-y-3 sm:grid-cols-2">
            <div class="sm:col-span-2">
              <dt hlmMuted class="text-xs">Straße & Hausnummer</dt>
              <dd class="text-sm">{{ billingAddress.street || '–' }}</dd>
            </div>
            <div>
              <dt hlmMuted class="text-xs">Stadt</dt>
              <dd class="text-sm">{{ billingAddress.city || '–' }}</dd>
            </div>
            <div>
              <dt hlmMuted class="text-xs">Postleitzahl</dt>
              <dd class="text-sm">{{ billingAddress.zip || '–' }}</dd>
            </div>
            <div class="sm:col-span-2">
              <dt hlmMuted class="text-xs">Land</dt>
              <dd class="text-sm">{{ getCountryName(billingAddress.country) }}</dd>
            </div>
          </dl>
        } @else {
          <div class="grid gap-4 sm:grid-cols-2">
            <div hlmField class="sm:col-span-2">
              <label hlmFieldLabel for="business_street">Straße & Hausnummer *</label>
              <input
                hlmInput
                id="business_street"
                type="text"
                formControlName="business_street"
                placeholder="Geschäftsstraße 1"
                class="w-full"
                [attr.aria-invalid]="isInvalid('business_street') ? 'true' : null" />
              @if (isInvalid('business_street')) {
                <p hlmFieldDescription class="text-destructive">Straße ist erforderlich</p>
              }
            </div>

            <div hlmField>
              <label hlmFieldLabel for="business_city">Stadt *</label>
              <input
                hlmInput
                id="business_city"
                type="text"
                formControlName="business_city"
                placeholder="Musterstadt"
                class="w-full"
                [attr.aria-invalid]="isInvalid('business_city') ? 'true' : null" />
              @if (isInvalid('business_city')) {
                <p hlmFieldDescription class="text-destructive">Stadt ist erforderlich</p>
              }
            </div>

            <div hlmField>
              <label hlmFieldLabel for="business_zip">Postleitzahl *</label>
              <input
                hlmInput
                id="business_zip"
                type="text"
                inputmode="numeric"
                formControlName="business_zip"
                placeholder="12345"
                class="w-full"
                [attr.aria-invalid]="isInvalid('business_zip') ? 'true' : null" />
              @if (isInvalid('business_zip')) {
                <p hlmFieldDescription class="text-destructive">Postleitzahl ist erforderlich</p>
              }
            </div>

            <div hlmField class="sm:col-span-2">
              <label hlmFieldLabel for="business_country">Land *</label>
              <hlm-native-select
                selectId="business_country"
                class="w-full"
                formControlName="business_country">
                @for (country of countries; track country.code) {
                  <option hlmNativeSelectOption [value]="country.code">{{ country.name }}</option>
                }
              </hlm-native-select>
            </div>
          </div>
        }
      </div>

      <div hlmCardFooter class="justify-end border-t pt-4">
        <app-save-button
          #businessAddressSaveButton
          size="sm"
          [disabled]="!businessAddressChanged"
          [loading]="loading"
          (clickEvent)="onSubmitBusinessAddress()" />
      </div>
    </section>

    <!-- ── Further locations: plain shops only, a network shows its members ── -->
    @if (isPlainShop) {
      <app-store-locations />
    }

    <!-- ── Preview ── -->
    <section hlmCard>
      <div hlmCardHeader>
        <h2 hlmCardTitle>Shop-Vorschau</h2>
        <p hlmCardDescription>So sieht dein Gutschein-Shop für Kunden aus.</p>
      </div>

      <div hlmCardContent>
        <div class="border-border overflow-hidden rounded-lg border">
          @if (currentBannerPath) {
            <img [src]="getImageUrl(currentBannerPath)" alt="" class="h-40 w-full object-cover" />
          } @else {
            <div
              class="bg-muted text-muted-foreground flex h-40 items-center justify-center text-sm">
              Banner wird hier angezeigt
            </div>
          }

          <div class="space-y-3 p-6 text-center">
            <div class="flex items-center justify-center gap-4">
              @if (currentLogoPath) {
                <img
                  [src]="getImageUrl(currentLogoPath)"
                  alt=""
                  class="border-background size-16 rounded-full border-2 object-cover" />
              } @else {
                <div
                  class="bg-muted text-muted-foreground flex size-16 items-center justify-center rounded-full">
                  <ng-icon name="lucideImage" />
                </div>
              }
              <h3 hlmH4>{{ companyName || 'Dein Shop' }}</h3>
            </div>

            <p hlmMuted class="text-sm">
              {{ storefrontForm.get('description')?.value || 'Deine Shop-Beschreibung…' }}
            </p>

            <span hlmBadge variant="secondary">Gutschein-Kaufbereich</span>
          </div>
        </div>

        <p hlmMuted class="mt-3 text-xs">
          Name, Logo, Banner und Farben übernimmt die Webseite aus
          <a
            [routerLink]="[]"
            [queryParams]="{ tab: 'brand' }"
            class="text-foreground underline underline-offset-4"
            >Marke &amp; Branding</a
          >.
        </p>
      </div>
    </section>

    <!-- ── Activation ── -->
    <section hlmCard>
      <div hlmCardHeader>
        <h2 hlmCardTitle>Shop-Aktivierung</h2>
      </div>

      <div hlmCardContent>
        <div hlmItem variant="outline">
          <div hlmItemContent>
            <span hlmItemTitle>Gutschein-Shop aktivieren</span>
            <span hlmItemDescription>Mache deinen Gutschein-Shop für Kunden sichtbar.</span>
          </div>
          <div hlmItemActions>
            <hlm-switch
              aria-label="Gutschein-Shop aktivieren"
              formControlName="is_storefront_enabled" />
          </div>
        </div>
      </div>

      <div hlmCardFooter class="justify-end border-t pt-4">
        <app-save-button
          #activationSaveButton
          size="sm"
          [disabled]="!storefrontForm.valid || !formChanged"
          [loading]="loading"
          (clickEvent)="onSubmitActivationSettings()" />
      </div>
    </section>
  </form>
</div>
